// @ts-nocheck
/**
 * The eight per-division screens. Every one of these is an existing panel from
 * items 1–17, scoped to the division picked in the sidebar — this is a
 * rearrangement, not new behaviour.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowLeftRight, ExternalLink } from "lucide-react";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { useDivisionContext } from "./context";
import DivisionAdvancedSettings from "@/components/admin/schedule/DivisionAdvancedSettings";
import PreventedMatchups from "@/components/admin/schedule/PreventedMatchups";
import PoolsPanel, { PoolRoundRobin, PoolPrintPage } from "@/components/admin/schedule/PoolsPanel";
import BracketsPanel from "@/components/admin/schedule/BracketsPanel";
import BulkEditGrid from "@/components/admin/schedule/BulkEditGrid";
import RenumberGamesButton from "@/components/admin/schedule/RenumberGamesButton";
import DivisionPlaces from "@/components/admin/schedule/DivisionPlaces";
import TeamDataGrids from "@/components/admin/teams/TeamDataGrids";
import SwapTeamsDialog from "@/components/admin/schedule/SwapTeamsDialog";

/* ── Division Details ─────────────────────────────────────────── */
export function DivisionDetailsScreen() {
  const { eventId, division, counts, refreshCounts } = useDivisionContext();
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Division details</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
          <div><span className="text-muted-foreground">Name</span><div className="font-medium">{division}</div></div>
          <div><span className="text-muted-foreground">Gender</span><div className="font-medium">{counts?.gender ?? "—"}</div></div>
          <div><span className="text-muted-foreground">Entry fee</span><div className="font-medium">{counts?.entry_fee != null ? `$${Number(counts.entry_fee).toFixed(2)}` : "Event default"}</div></div>
          <div><span className="text-muted-foreground">Max teams</span><div className="font-medium">{counts?.max_teams ?? "No limit"}</div></div>
          <div className="sm:col-span-2 text-xs text-muted-foreground">
            Name, gender, entry fee and max teams are set with the rest of the event —{" "}
            <Link className="underline" to={`/admin-dashboard/events/${eventId}/edit?step=divisions`}>edit them in the event details</Link>.
            Abbreviation is not stored anywhere in this product.
          </div>
        </CardContent>
      </Card>
      <DivisionAdvancedSettings eventId={eventId} division={division} onSaved={refreshCounts} />
      <PreventedMatchups eventId={eventId} division={division} onChanged={refreshCounts} />
    </div>
  );
}

/* ── Teams ────────────────────────────────────────────────────── */
export function DivisionTeamsScreen() {
  const { eventId, division, refreshCounts } = useDivisionContext();
  const [swapOpen, setSwapOpen] = useState(false);
  const [teams, setTeams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(0);

  useEffect(() => {
    let dead = false;
    (async () => {
      setLoading(true);
      const { data } = await invokeEdgeFunction("rle-operator-schedule-manual", {
        body: { action: "pools_data", event_id: eventId },
      });
      if (dead) return;
      const d = (data?.data?.divisions ?? []).find((x: any) => x.division === division);
      setTeams(
        (d?.pools ?? []).flatMap((p: any) =>
          (p.teams ?? []).map((t: any) => ({
            registration_id: t.registration_id,
            team_name: t.name,
            pool: p.name,
          })),
        ),
      );
      setLoading(false);
    })();
    return () => { dead = true; };
  }, [eventId, division, token]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setSwapOpen(true)} disabled={loading || teams.length < 2}>
          <ArrowLeftRight className="mr-2 h-4 w-4" /> Swap two teams
        </Button>
        <Button size="sm" variant="ghost" asChild>
          <Link to={`/admin-dashboard/events/${eventId}/divisions/${encodeURIComponent(division)}/restrictions`}>
            Per-team schedule requests <ExternalLink className="ml-1 h-3.5 w-3.5" />
          </Link>
        </Button>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      <TeamDataGrids eventId={eventId} division={division} refreshToken={token} />
      <SwapTeamsDialog
        open={swapOpen}
        onOpenChange={setSwapOpen}
        eventId={eventId}
        division={division}
        teams={teams}
        onChanged={() => { setToken((t) => t + 1); refreshCounts(); }}
      />
    </div>
  );
}

/* ── Pools ────────────────────────────────────────────────────── */
function poolsBase(eventId: string, division: string) {
  return `/admin-dashboard/events/${eventId}/divisions/${encodeURIComponent(division)}/pools`;
}

export function DivisionPoolsScreen() {
  const { eventId, division, refreshCounts } = useDivisionContext();
  const navigate = useNavigate();
  return (
    <PoolsPanel
      eventId={eventId}
      division={division}
      view="list"
      onOpenAssign={() => navigate(`${poolsBase(eventId, division)}/assign`)}
      onOpenRoundRobin={(poolId) => navigate(`${poolsBase(eventId, division)}/${poolId}/round-robin`)}
      onOpenPrint={(poolId) => window.open(`${poolsBase(eventId, division)}/${poolId}/print`, "_blank", "noopener")}
      onChanged={refreshCounts}
    />
  );
}

export function DivisionPoolsAssignScreen() {
  const { eventId, division, refreshCounts } = useDivisionContext();
  const navigate = useNavigate();
  return (
    <PoolsPanel
      eventId={eventId}
      division={division}
      view="assign"
      onBack={() => navigate(poolsBase(eventId, division))}
      onChanged={refreshCounts}
    />
  );
}

export function DivisionPoolRoundRobinScreen() {
  const { eventId, division, refreshCounts } = useDivisionContext();
  const { poolId } = useParams();
  const navigate = useNavigate();
  return (
    <PoolRoundRobin
      eventId={eventId}
      poolId={poolId}
      onBack={() => navigate(poolsBase(eventId, division))}
      onDone={() => {
        refreshCounts();
        navigate(`/admin-dashboard/events/${eventId}/divisions/${encodeURIComponent(division)}/pool-games`);
      }}
    />
  );
}

/** Print page — routed outside the admin layout, so it reads the URL directly. */
export function DivisionPoolPrintScreen() {
  const { eventId, division, poolId } = useParams();
  return <PoolPrintPage eventId={eventId} division={division ?? ""} poolId={poolId} />;
}

/* ── Pool Games ───────────────────────────────────────────────── */
export function DivisionPoolGamesScreen() {
  const { eventId, division, refreshCounts } = useDivisionContext();
  const [token, setToken] = useState(0);
  return (
    <BulkEditGrid
      key={token}
      eventId={eventId}
      division={division}
      gameType="pool"
      onChanged={refreshCounts}
      extraActions={
        <RenumberGamesButton
          eventId={eventId}
          division={division}
          onDone={() => { setToken((t) => t + 1); refreshCounts(); }}
        />
      }
    />
  );
}

/* ── Bracket Games ────────────────────────────────────────────── */
export function DivisionBracketGamesScreen() {
  const { eventId, division, refreshCounts } = useDivisionContext();
  return <BulkEditGrid eventId={eventId} division={division} gameType="bracket" onChanged={refreshCounts} />;
}

/* ── Bracket (templates + advance teams) ──────────────────────── */
export function DivisionBracketScreen() {
  const { eventId, division, versionId, refreshCounts } = useDivisionContext();
  if (!versionId) {
    return (
      <Card><CardContent className="py-8 text-sm text-muted-foreground">
        Generate a draft schedule first — brackets hang off a schedule version.
      </CardContent></Card>
    );
  }
  return <BracketsPanel eventId={eventId} versionId={versionId} division={division} canManage onChanged={refreshCounts} />;
}

/* ── Schedule Restrictions ────────────────────────────────────── */
export function DivisionRestrictionsScreen() {
  const { eventId, division, counts } = useDivisionContext();
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          Schedule restrictions
          <Badge variant="outline">{counts?.restrictions ?? 0}</Badge>
          {counts?.restrictions_pending > 0 && (
            <Badge className="bg-amber-500 text-white">{counts.restrictions_pending} pending</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          Requests from {division} coaches — blackouts, must-play windows and do-not-play pairs.
        </p>
        <Button size="sm" asChild>
          <Link to={`/admin-dashboard/events/${eventId}/scheduling-requests?division=${encodeURIComponent(division)}`}>
            Review {division} requests <ExternalLink className="ml-1 h-3.5 w-3.5" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

/* ── Set Division Places ──────────────────────────────────────── */
export function DivisionPlacesScreen() {
  const { eventId, division } = useDivisionContext();
  return <DivisionPlaces eventId={eventId} division={division} />;
}
